/**
 * Trade Distribution Components
 * Version: 6.2.0
 * 
 * Trade analysis: histograms, heatmaps, sector breakdown
 */
import './TradeDistribution.css'

// ============================================
// Histogram Component
// ============================================
interface HistogramData {
  label: string
  count: number
  value?: number
}

interface HistogramProps {
  data: HistogramData[]
  title?: string
  showValues?: boolean
  binSize?: number
}

function Histogram({ data, title, showValues = true, binSize = 50 }: HistogramProps) {
  const maxCount = Math.max(...data.map(d => d.count))
  
  return (
    <div className="histogram">
      {title && <h4 className="histogram-title">{title}</h4>}
      <div className="histogram-bars">
        {data.map((d, i) => {
          const heightPercent = (d.count / maxCount) * 100
          const isPositive = i >= Math.floor(data.length / 2)
          
          return (
            <div key={d.label} className="histogram-bar-container">
              <div className="histogram-bar-wrapper">
                <div 
                  className={`histogram-bar ${isPositive ? 'positive' : 'negative'}`}
                  style={{ height: `${heightPercent}%` }}
                >
                  {showValues && heightPercent > 20 && (
                    <span className="bar-count">{d.count}</span>
                  )}
                </div>
              </div>
              <span className="histogram-label">{d.label}</span>
            </div>
          )
        })}
      </div>
      <div className="histogram-axis">
        <span>-{binSize * Math.ceil(data.length / 2)}</span>
        <span>0</span>
        <span>+{binSize * Math.ceil(data.length / 2)}</span>
      </div>
    </div>
  )
}

// ============================================
// Heatmap Component
// ============================================
interface HeatmapCell {
  label: string
  value: number
  tooltip?: string
}

interface HeatmapProps {
  data: HeatmapCell[][]
  rowLabels: string[]
  colLabels: string[]
  title?: string
  colorScale?: 'green' | 'red' | 'blue'
}

function Heatmap({ data, rowLabels, colLabels, title, colorScale = 'green' }: HeatmapProps) {
  const maxValue = Math.max(...data.flat().map(d => Math.abs(d.value)))
  
  const getColor = (value: number) => {
    const intensity = Math.abs(value) / maxValue
    const alpha = 0.2 + intensity * 0.8
    
    if (value >= 0) {
      switch (colorScale) {
        case 'green': return `rgba(16, 185, 129, ${alpha})`
        case 'blue': return `rgba(99, 102, 241, ${alpha})`
        default: return `rgba(16, 185, 129, ${alpha})`
      }
    } else {
      return `rgba(239, 68, 68, ${alpha})`
    }
  }

  return (
    <div className="heatmap">
      {title && <h4 className="heatmap-title">{title}</h4>}
      <div className="heatmap-grid">
        <div className="heatmap-col-labels">
          <span></span>
          {colLabels.map(label => (
            <span key={label} className="col-label">{label}</span>
          ))}
        </div>
        {data.map((row, i) => (
          <div key={rowLabels[i]} className="heatmap-row">
            <span className="row-label">{rowLabels[i]}</span>
            {row.map((cell, j) => (
              <div 
                key={`${i}-${j}`}
                className="heatmap-cell"
                style={{ backgroundColor: getColor(cell.value) }}
                title={cell.tooltip || `${cell.label}: ${cell.value}`}
              >
                <span className="cell-value">{cell.value > 0 ? '+' : ''}{cell.value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="heatmap-legend">
        <span className="legend-negative">Loss</span>
        <div className="legend-gradient"></div>
        <span className="legend-positive">Profit</span>
      </div>
    </div>
  )
}

// ============================================
// Sector Breakdown Component
// ============================================
interface SectorData {
  name: string
  value: number
  count: number
  color?: string
}

interface SectorBreakdownProps {
  data: SectorData[]
  title?: string
  showPercentage?: boolean
}

function SectorBreakdown({ data, title, showPercentage = true }: SectorBreakdownProps) {
  const totalValue = data.reduce((sum, d) => sum + Math.abs(d.value), 0)
  const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#ef4444', '#f97316']

  return (
    <div className="sector-breakdown">
      {title && <h4 className="sector-title">{title}</h4>}
      <div className="sector-list">
        {data.map((sector, i) => {
          const percentage = (Math.abs(sector.value) / totalValue) * 100
          const isProfit = sector.value >= 0
          
          return (
            <div key={sector.name} className="sector-item">
              <div className="sector-info">
                <div 
                  className="sector-color"
                  style={{ backgroundColor: sector.color || colors[i % colors.length] }}
                />
                <span className="sector-name">{sector.name}</span>
                {showPercentage && (
                  <span className="sector-percentage">{percentage.toFixed(1)}%</span>
                )}
              </div>
              <div className="sector-bar-container">
                <div 
                  className="sector-bar"
                  style={{ 
                    width: `${percentage}%`,
                    backgroundColor: sector.color || colors[i % colors.length]
                  }}
                />
              </div>
              <div className="sector-values">
                <span className={`sector-value ${isProfit ? 'profit' : 'loss'}`}>
                  {isProfit ? '+' : ''}₹{sector.value.toLocaleString()}
                </span>
                <span className="sector-count">{sector.count} trades</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Time Distribution Component
// ============================================
interface TimeDistributionData {
  hour: number
  winRate: number
  trades: number
  avgPnl: number
}

interface TimeDistributionProps {
  data: TimeDistributionData[]
  title?: string
  view?: 'hourly' | 'daily'
}

function TimeDistribution({ data, title, view = 'hourly' }: TimeDistributionProps) {
  const maxTrades = Math.max(...data.map(d => d.trades))
  
  return (
    <div className="time-distribution">
      {title && <h4 className="time-title">{title}</h4>}
      <div className="time-grid">
        {data.map((d) => {
          const heightPercent = (d.trades / maxTrades) * 100
          const color = d.winRate >= 50 ? 'var(--success)' : 'var(--danger)'
          
          return (
            <div key={d.hour} className="time-bar" title={`${d.trades} trades, ${d.winRate}% win rate`}>
              <div className="time-bar-inner">
                <div 
                  className="time-bar-fill"
                  style={{ 
                    height: `${heightPercent}%`,
                    backgroundColor: color
                  }}
                />
              </div>
              <span className="time-label">
                {view === 'hourly' ? `${d.hour}:00` : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d.hour]}
              </span>
              <span className="time-winrate">{d.winRate}%</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Trade Duration Analysis
// ============================================
interface DurationData {
  range: string
  count: number
  avgPnl: number
  winRate: number
}

interface DurationAnalysisProps {
  data: DurationData[]
  title?: string
}

function DurationAnalysis({ data, title }: DurationAnalysisProps) {
  const maxCount = Math.max(...data.map(d => d.count))
  
  return (
    <div className="duration-analysis">
      {title && <h4 className="duration-title">{title}</h4>}
      <div className="duration-list">
        {data.map((d) => {
          const widthPercent = (d.count / maxCount) * 100
          const isProfit = d.avgPnl >= 0
          
          return (
            <div key={d.range} className="duration-item">
              <div className="duration-header">
                <span className="duration-range">{d.range}</span>
                <span className={`duration-pnl ${isProfit ? 'profit' : 'loss'}`}>
                  {isProfit ? '+' : ''}₹{d.avgPnl}
                </span>
              </div>
              <div className="duration-bar-container">
                <div 
                  className="duration-bar"
                  style={{ width: `${widthPercent}%` }}
                />
              </div>
              <div className="duration-stats">
                <span>{d.count} trades</span>
                <span>{d.winRate}% win</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Export
// ============================================
export { 
  Histogram,
  Heatmap,
  SectorBreakdown,
  TimeDistribution,
  DurationAnalysis
}

export type { 
  HistogramData,
  HeatmapCell,
  SectorData,
  TimeDistributionData,
  DurationData
}